import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchPlayableHans, fetchTravelPath } from './api'
import { formatHanLabel } from '../../components/TerritoryGrid/territoryUtils'
import './TravelItineraryPicker.css'

const ROUTE_TYPE_LABEL = {
  safe: 'Segura',
  fast: 'Rápida',
  dangerous: 'Peligrosa',
}

/**
 * Selector de destino multi-Han + vias + preview de tramos.
 */
export default function TravelItineraryPicker({
  characterId,
  originSlug: originSlugProp = null,
  destSlug,
  onDestSlugChange,
  via = [],
  onViaChange,
  preferredRouteType = 'safe',
  onPreferredRouteTypeChange,
  disabled = false,
  idPrefix = 'travel-itin',
}) {
  const [hans, setHans] = useState([])
  const [originSlug, setOriginSlug] = useState(originSlugProp)
  const [legs, setLegs] = useState([])
  const [waypoints, setWaypoints] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [viaPick, setViaPick] = useState('')

  useEffect(() => {
    let active = true
    fetchPlayableHans()
      .then((list) => {
        if (active) setHans(list)
      })
      .catch(() => {
        if (active) setHans([])
      })
    return () => {
      active = false
    }
  }, [])

  const destOptions = useMemo(() => {
    const origin = originSlugProp || originSlug
    return hans.filter((h) => h.slug && h.slug !== origin)
  }, [hans, originSlug, originSlugProp])

  const refreshPath = useCallback(async () => {
    if (!characterId || !destSlug) {
      setLegs([])
      setWaypoints([])
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchTravelPath({
        characterId,
        to: destSlug,
        via,
        preferredRouteType,
      })
      setOriginSlug(data.originSlug || originSlugProp)
      setLegs(data.legs || [])
      setWaypoints(data.waypoints || [])
    } catch (e) {
      setLegs([])
      setWaypoints([])
      setError(e?.response?.data?.message || e?.message || 'No hay camino a ese destino')
    } finally {
      setLoading(false)
    }
  }, [characterId, destSlug, via, preferredRouteType, originSlugProp])

  useEffect(() => {
    refreshPath()
  }, [refreshPath])

  useEffect(() => {
    if (!destSlug && destOptions.length) {
      onDestSlugChange(destOptions[0].slug)
    }
  }, [destOptions, destSlug, onDestSlugChange])

  const viaOptions = useMemo(() => {
    const blocked = new Set([originSlug, destSlug, ...via].filter(Boolean))
    return hans.filter((h) => h.slug && !blocked.has(h.slug))
  }, [hans, originSlug, destSlug, via])

  const addVia = () => {
    if (!viaPick || !onViaChange) return
    if (via.includes(viaPick)) return
    onViaChange([...via, viaPick])
    setViaPick('')
  }

  const removeVia = (slug) => {
    if (!onViaChange) return
    onViaChange(via.filter((v) => v !== slug))
  }

  return (
    <div className="travel-itin">
      <div className="travel-itin__fields">
        <div className="travel-itin__row">
          <label htmlFor={`${idPrefix}-dest`}>Destino</label>
          <select
            id={`${idPrefix}-dest`}
            value={destSlug || ''}
            onChange={(e) => onDestSlugChange(e.target.value)}
            disabled={disabled || loading}
          >
            {!destSlug ? <option value="">Elige un Han…</option> : null}
            {destOptions.map((h) => (
              <option key={h.slug} value={h.slug}>
                {h.name || formatHanLabel(h.slug)}
              </option>
            ))}
          </select>
        </div>

        <div className="travel-itin__row">
          <label htmlFor={`${idPrefix}-pref`}>Preferencia de ruta</label>
          <select
            id={`${idPrefix}-pref`}
            value={preferredRouteType || 'safe'}
            onChange={(e) => onPreferredRouteTypeChange(e.target.value)}
            disabled={disabled || loading}
          >
            <option value="safe">{ROUTE_TYPE_LABEL.safe}</option>
            <option value="fast">{ROUTE_TYPE_LABEL.fast}</option>
          </select>
        </div>
      </div>

      <div className="travel-itin__via">
        <label htmlFor={`${idPrefix}-via`}>Pasar por</label>
        <div className="travel-itin__via-row">
          <select
            id={`${idPrefix}-via`}
            value={viaPick}
            onChange={(e) => setViaPick(e.target.value)}
            disabled={disabled || loading || viaOptions.length === 0}
          >
            <option value="">Han intermedio…</option>
            {viaOptions.map((h) => (
              <option key={h.slug} value={h.slug}>
                {h.name || formatHanLabel(h.slug)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="travel-itin__via-add"
            onClick={addVia}
            disabled={disabled || !viaPick}
          >
            Añadir
          </button>
        </div>
        {via.length > 0 ? (
          <ul className="travel-itin__via-list">
            {via.map((slug) => (
              <li key={slug}>
                <span>{formatHanLabel(slug)}</span>
                <button type="button" onClick={() => removeVia(slug)} disabled={disabled}>
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {loading ? <p className="travel-itin__muted">Calculando camino…</p> : null}
      {error ? <p className="travel-itin__error">{error}</p> : null}

      {!loading && !error && waypoints.length >= 2 ? (
        <div className="travel-itin__preview" aria-label="Itinerario">
          <p className="travel-itin__preview-path">
            {waypoints.map((slug, i) => (
              <React.Fragment key={`${slug}-${i}`}>
                {i > 0 ? <span className="travel-itin__arrow"> → </span> : null}
                <span>{formatHanLabel(slug)}</span>
              </React.Fragment>
            ))}
          </p>
          <ul className="travel-itin__legs">
            {legs.map((leg, i) => (
              <li
                key={`${leg.originSlug}-${leg.destSlug}-${i}`}
                className={
                  leg.routeType === 'dangerous' ? 'travel-itin__leg travel-itin__leg--danger' : 'travel-itin__leg'
                }
              >
                <span>
                  {formatHanLabel(leg.originSlug)} → {formatHanLabel(leg.destSlug)}
                </span>
                <span className="travel-itin__leg-type">
                  {ROUTE_TYPE_LABEL[leg.routeType] || leg.routeType}
                </span>
              </li>
            ))}
          </ul>
          <p className="travel-itin__hint">
            {legs.length} tramo{legs.length === 1 ? '' : 's'}
            {legs.some((l) => l.routeType === 'dangerous')
              ? ' · tramos en Guerra forzados a Peligrosa'
              : ''}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export { ROUTE_TYPE_LABEL }
